import Button from "./ds/Button";
import Modal from "./ds/Modal";
import { useState, useEffect } from "react";
import { joinMintSpace } from "../api/mintSpace";
import { useTranslation } from "../context/TranslationContext";

const JoinRequestModal = ({ isOpen, setIsOpen, mintSpace, isDark, setToast, onSuccess }) => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const inputBase = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  // Initialize answers when modal opens
  useEffect(() => {
    if (isOpen && mintSpace?.questions) {
      setAnswers(mintSpace.questions.map(() => ""));
    }
  }, [isOpen, mintSpace]);

  const handleAnswerChange = (index, value) => {
    const updated = [...answers];
    updated[index] = value;
    setAnswers(updated);
  };

    const handleSubmit = async () => {
    // Validate all questions are answered
    if (answers.some((answer) => !answer.trim())) {
      setToast(t("mintSpace.pleaseAnswerAllQuestions"));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await joinMintSpace(mintSpace._id, answers);
      if (result.success) {
        setToast(t("mintSpace.joinRequestSubmitted"));
        setIsOpen(false);
        // Dispatch event to refresh sidebar
        window.dispatchEvent(new CustomEvent('mintSpaceJoined', { detail: { mintSpaceId: mintSpace._id } }));
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      setToast(error.response?.data?.message || error.message || t("mintSpace.errorSubmittingRequest"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoin = async () => {
    setIsSubmitting(true);
    try {
      const result = await joinMintSpace(mintSpace._id);
      if (result.success) {
        setToast(t("mintSpace.successfullyJoined"));
        setIsOpen(false);
        // Dispatch event to refresh sidebar
        window.dispatchEvent(new CustomEvent('mintSpaceJoined', { detail: { mintSpaceId: mintSpace._id } }));
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      setToast(error.response?.data?.message || error.message || t("mintSpace.errorJoining"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !mintSpace) return null;

  const hasQuestions = mintSpace.questions && mintSpace.questions.length > 0;

  return (
    <Modal
      open
      onClose={() => setIsOpen(false)}
      isDark={isDark}
      title={hasQuestions ? "Request to Join" : "Join Mint Space"}
      description={
        hasQuestions
          ? "Please answer the following questions to request access"
          : "Join this mint space to access all content"
      }
    >
      {hasQuestions ? (
        <div className="flex max-h-96 flex-col gap-space-16 overflow-y-auto">
          {mintSpace.questions.map((question, index) => (
            <label key={index} className="flex flex-col gap-space-4">
              <span className="text-footnote-semibold text-label-secondary">{question}</span>
              <textarea
                value={answers[index] || ""}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                placeholder="Your answer..."
                rows={3}
                className="w-full resize-none rounded-[22px] bg-fill-tertiary px-space-16 py-space-12 text-callout text-label-primary outline-none placeholder:text-label-tertiary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
              />
            </label>
          ))}
        </div>
      ) : (
        <p className="text-subheadline text-label-secondary">You can join this mint space freely.</p>
      )}

      <div className="flex justify-end gap-space-8">
        <Button variant="gray" size="m" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <Button
          size="m"
          onClick={hasQuestions ? handleSubmit : handleJoin}
          loading={isSubmitting}
          disabled={hasQuestions && answers.some((a) => !a.trim())}
        >
          {isSubmitting ? "Submitting..." : hasQuestions ? "Submit Request" : "Join"}
        </Button>
      </div>
    </Modal>
  );
};

export default JoinRequestModal;

